import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Trash2 } from 'lucide-react';
import {
  ResponsiveModal, ResponsiveModalContent, ResponsiveModalHeader, ResponsiveModalTitle, ResponsiveModalClose,
  ResponsiveModalBody, ResponsiveModalFooter,
} from '../ui/responsive-modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ModelSelect from './ModelSelect';
import BotAvatar from './BotAvatar';
import { useCreateBot, useDeleteBot, useMoveBot, useUpdateBot } from '../../hooks/mutations';
import { useMyBotsQuery } from '../../hooks/queries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { detailToMessage } from '../../lib/apiError';
import { botDisplayName, botDisplayRole, useBotLabel } from './botDisplay';
import ConfirmModal from '../ConfirmModal';

const COLORS = ['#0284c7', '#059669', '#e11d48', '#7c3aed', '#d97706', '#0891b2', '#db2777', '#4f46e5'];

/**
 * Create or edit a bot. bot = null creates a bot in the given scope
 * (teamId = null means a personal bot). Editing also offers deleting it (onDeleted
 * after that, e.g. to leave the deleted bot's page).
 */
const BotFormModal = ({ open, onClose, bot, teamId, models, inheritLabel, onSaved, onDeleted }) => {
  const { t } = useTranslation();
  const botLabel = useBotLabel();
  const createBot = useCreateBot();
  const updateBot = useUpdateBot();
  const moveBot = useMoveBot();
  const deleteBot = useDeleteBot();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: mine } = useMyBotsQuery({ enabled: open && !!bot });
  const [form, setForm] = useState({ name: '', description: '', persona: '', default_model: null, avatar_color: null });
  // Who the bot belongs to: the person or a team they administer.
  const scopeKey = (s) => `${s.type}:${s.id}`;
  const ownerKey = bot ? `${bot.owner_type}:${bot.owner_id}` : null;
  const [owner, setOwner] = useState(null);
  const owners = (mine?.scopes || []).filter((s) => s.can_edit);
  const canMove = !!bot && owners.length > 1 && owners.some((s) => scopeKey(s) === ownerKey);
  const ownerName = (s) => (s.type === 'team' ? s.name : t('bots.scope_personal'));

  useEffect(() => {
    if (!open) return;
    setForm({
      name: bot ? botDisplayName(bot, t) : '',
      description: bot ? botDisplayRole(bot, t) : '',
      persona: bot?.persona || '',
      default_model: bot?.default_model ?? null,
      avatar_color: bot?.avatar_color ?? null,
    });
    setOwner(ownerKey);
  }, [open, bot, t, ownerKey]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const saving = createBot.isPending || updateBot.isPending || moveBot.isPending;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const data = { ...form, name: form.name.trim() };
    if (!data.avatar_color) delete data.avatar_color;
    // An old automatic Assistant keeps its name (and role) translated until it is renamed;
    // once renamed, the role shown now is kept as its own.
    if (bot?.name_is_default && data.name === botDisplayName(bot, t)) delete data.name;
    if (bot?.name_is_default && !('name' in data) && !bot.description && data.description === botDisplayRole(bot, t)) {
      delete data.description;
    }
    try {
      if (bot && canMove && owner && owner !== ownerKey) {
        const target = owners.find((s) => scopeKey(s) === owner);
        await moveBot.mutateAsync({ id: bot.id, teamId: target?.type === 'team' ? target.id : null });
      }
      const saved = bot
        ? await updateBot.mutateAsync({ id: bot.id, data })
        : await createBot.mutateAsync({ ...data, team_id: teamId || null });
      if (onSaved) {
        await onSaved(saved);
        return;
      }
      toast.success(t('bots.saved'));
      onClose();
    } catch (err) {
      toast.error(detailToMessage(err?.response?.data?.detail) || t('common.error'));
    }
  };

  const remove = async () => {
    try {
      await deleteBot.mutateAsync(bot.id);
      toast.success(t('bots.deleted'));
      setConfirmDelete(false);
      onClose();
      onDeleted?.(bot);
    } catch (err) {
      toast.error(detailToMessage(err?.response?.data?.detail) || t('common.error'));
      setConfirmDelete(false);
    }
  };

  return (
    <>
    <ResponsiveModal open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <ResponsiveModalContent>
        <form onSubmit={submit} className="contents">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle className="flex items-center gap-3">
              <BotAvatar bot={{ id: bot?.id, name: form.name || '?', avatar_color: form.avatar_color || COLORS[0] }} />
              {bot ? t('bots.edit_bot') : t('bots.create_bot')}
            </ResponsiveModalTitle>
            <ResponsiveModalClose className="text-muted-foreground hover:bg-accent hover:text-foreground p-2" />
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bot-name">{t('bots.field_name')}</Label>
              <Input id="bot-name" value={form.name} maxLength={60} autoFocus
                onChange={(e) => set('name')(e.target.value)} />
            </div>
            {canMove && (
              <div className="space-y-1.5">
                <Label htmlFor="bot-owner">{t('bots.field_owner')}</Label>
                <Select value={owner || undefined} onValueChange={setOwner}>
                  <SelectTrigger id="bot-owner">
                    <SelectValue>{ownerName(owners.find((s) => scopeKey(s) === owner) || owners[0])}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {owners.map((s) => <SelectItem key={scopeKey(s)} value={scopeKey(s)}>{ownerName(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="bot-description">{t('bots.field_description')}</Label>
              <Input id="bot-description" value={form.description} maxLength={200}
                placeholder={t('bots.field_description_placeholder')}
                onChange={(e) => set('description')(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bot-persona">{t('bots.field_persona')}</Label>
              <Textarea id="bot-persona" value={form.persona} maxLength={4000} rows={5}
                placeholder={t('bots.field_persona_placeholder')}
                onChange={(e) => set('persona')(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bot-model">{t('bots.field_model')}</Label>
              <ModelSelect id="bot-model" models={models} value={form.default_model}
                onChange={set('default_model')} inheritLabel={inheritLabel} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('bots.field_color')}</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => set('avatar_color')(c)}
                    className="w-7 h-7 rounded-md flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{ backgroundColor: c }} aria-label={c} aria-pressed={form.avatar_color === c}>
                    {form.avatar_color === c && <Check className="w-4 h-4 text-foreground" />}
                  </button>
                ))}
              </div>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            {bot && (
              <Button type="button" variant="ghost" className="md:mr-auto gap-1.5 text-destructive hover:text-destructive"
                disabled={saving || deleteBot.isPending} onClick={() => setConfirmDelete(true)} data-testid="bot-delete">
                <Trash2 className="w-4 h-4" />{t('common.delete')}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={saving || !form.name.trim()}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
    {bot && (
      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title={t('bots.delete_title')}
        message={t('bots.delete_message', { name: botLabel(bot) })}
        confirmLabel={t('common.delete')}
        danger
      />
    )}
    </>
  );
};

export default BotFormModal;
